// src/api/bancos.js — API de Bancos, Cuentas y Pagos con Pago Móvil
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

// ==================== CONSTANTES ====================

export const TIPOS_CUENTA = [
  { value: 'ahorro',     label: 'Ahorro' },
  { value: 'corriente',  label: 'Corriente' },
  { value: 'digital',    label: 'Digital' },
  { value: 'wallet',     label: 'Wallet' },
  { value: 'otro',       label: 'Otro' }
];

export const MONEDAS = [
  { value: 'VES',  label: 'Bolívares (VES)' },
  { value: 'USD',  label: 'Dólares (USD)' },
  { value: 'USDT', label: 'USDT' },
  { value: 'EUR',  label: 'Euros (EUR)' }
];

export const TIPOS_DOCUMENTO_TITULAR = [
  { value: 'cedula',   label: 'Cédula' },
  { value: 'rif',      label: 'RIF' },
  { value: 'pasaporte', label: 'Pasaporte' },
  { value: 'otro',     label: 'Otro' }
];

export const METODOS_PAGO = [
  { value: 'efectivo',      label: 'Efectivo',      icon: 'mdi:cash' },
  { value: 'transferencia', label: 'Transferencia', icon: 'mdi:bank-transfer' },
  { value: 'pago_movil',    label: 'Pago Móvil',    icon: 'mdi:cellphone' },
  { value: 'zelle',         label: 'Zelle',         icon: 'mdi:alpha-z-circle' },
  { value: 'binance',       label: 'Binance',       icon: 'mdi:currency-btc' },
  { value: 'paypal',        label: 'PayPal',        icon: 'mdi:paypal' },
  { value: 'punto_venta',   label: 'Punto de Venta', icon: 'mdi:credit-card' },
  { value: 'otro',          label: 'Otro',          icon: 'mdi:dots-horizontal' }
];

export const TIPOS_DOCUMENTO_PAGO = [
  { value: 'venta',             label: 'Venta' },
  { value: 'compra',            label: 'Compra' },
  { value: 'salida_equipo',     label: 'Salida Equipo' },
  { value: 'servicio',          label: 'Servicio' },
  { value: 'servicio_externo',  label: 'Servicio Externo' },
  { value: 'credito',           label: 'Crédito' },
  { value: 'devolucion',        label: 'Devolución' },
  { value: 'prestamo',          label: 'Préstamo' },
  { value: 'gasto',             label: 'Gasto' },
  { value: 'movimiento_caja',   label: 'Movimiento Caja' }
];

// ==================== HELPERS ====================

export const formatearMetodoPago = (metodo) => {
  const found = METODOS_PAGO.find(m => m.value === metodo?.toLowerCase());
  return found ? found.label : (metodo || 'Sin método');
};

export const getMetodoPagoIcon = (metodo) => {
  const found = METODOS_PAGO.find(m => m.value === metodo?.toLowerCase());
  return found ? found.icon : 'mdi:cash';
};

export const formatearTipoCuenta = (tipo) => {
  const found = TIPOS_CUENTA.find(t => t.value === tipo?.toLowerCase());
  return found ? found.label : (tipo || '—');
};

export const formatearMoneda = (moneda) => {
  const found = MONEDAS.find(m => m.value === moneda?.toUpperCase());
  return found ? found.label : (moneda || '—');
};

export const formatearTipoDocumentoPago = (tipo) => {
  const found = TIPOS_DOCUMENTO_PAGO.find(t => t.value === tipo?.toLowerCase());
  return found ? found.label : (tipo || '—');
};

export const formatearDocumentoTitular = (tipo, documento) => {
  if (!documento) return '—';
  const prefix = {
    cedula: 'V', rif: 'J', pasaporte: 'P', otro: ''
  }[tipo?.toLowerCase()] || '';
  return prefix ? `${prefix}-${documento}` : documento;
};

export const formatearNumeroCuenta = (numero) => {
  if (!numero) return '—';
  const limpio = String(numero).replace(/\D/g, '');
  return limpio.replace(/(.{4})/g, '$1 ').trim();
};

export const formatearMonto = (monto, moneda = 'USD') => {
  const simbolo = { USD: '$', VES: 'Bs.', EUR: '€', USDT: '₮' }[moneda?.toUpperCase()] || '';
  const n = Number(monto || 0).toLocaleString('es-VE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  return `${simbolo} ${n}`.trim();
};

// ==================== BANCOS ====================

export const obtenerBancos = async () => {
  try {
    const data = await apiFetch('/Banco');
    return { success: true, data: data || [], count: (data || []).length };
  } catch (e) {
    console.error('❌ Error obteniendo bancos:', e);
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerBancosPagoMovil = async () => {
  try {
    const data = await apiFetch('/Banco/pago-movil');
    return { success: true, data: data || [], count: (data || []).length };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerBancoPorId = async (id) => {
  try {
    const data = await apiFetch(`/Banco/${id}`);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const crearBanco = async (payload) => {
  try {
    const data = await apiFetch('/Banco', { method: 'POST', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const actualizarBanco = async (id, payload) => {
  try {
    const data = await apiFetch(`/Banco/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const eliminarBanco = async (id) => {
  try {
    await apiFetch(`/Banco/${id}`, { method: 'DELETE' });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

export const buscarBancos = async (filtros = {}) => {
  try {
    const data = await apiFetch('/Banco/search', {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

// ==================== CUENTAS BANCARIAS ====================

export const obtenerCuentasBancarias = async () => {
  try {
    const data = await apiFetch('/CuentaBancaria');
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerCuentasPorBanco = async (idBanco) => {
  try {
    const data = await apiFetch(`/CuentaBancaria/banco/${idBanco}`);
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerCuentasPorDocumento = async (documento) => {
  try {
    const data = await apiFetch(`/CuentaBancaria/documento/${documento}`);
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerCuentasPropias = async () => {
  try {
    const data = await apiFetch('/CuentaBancaria/propias');
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerCuentaPorId = async (id) => {
  try {
    const data = await apiFetch(`/CuentaBancaria/${id}`);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const crearCuentaBancaria = async (payload) => {
  try {
    const data = await apiFetch('/CuentaBancaria', { method: 'POST', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const actualizarCuentaBancaria = async (id, payload) => {
  try {
    const data = await apiFetch(`/CuentaBancaria/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const eliminarCuentaBancaria = async (id) => {
  try {
    await apiFetch(`/CuentaBancaria/${id}`, { method: 'DELETE' });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ==================== PAGOS ↔ CUENTAS (con Pago Móvil) ====================

export const obtenerPagosCuenta = async () => {
  try {
    const data = await apiFetch('/PagoCuentaBancaria');
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerPagosPorDocumento = async (tipo, idDocumento) => {
  try {
    const data = await apiFetch(`/PagoCuentaBancaria/documento/${tipo}/${idDocumento}`);
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerPagosPorCuenta = async (idCuenta) => {
  try {
    const data = await apiFetch(`/PagoCuentaBancaria/cuenta/${idCuenta}`);
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

export const obtenerDetallePagoDocumento = async (tipo, idDocumento) => {
  try {
    const data = await apiFetch(`/PagoCuentaBancaria/documento/${tipo}/${idDocumento}/detalle`);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const obtenerResumenCuenta = async (idCuenta, desde = null, hasta = null) => {
  try {
    const params = new URLSearchParams();
    if (desde) params.append('desde', desde);
    if (hasta) params.append('hasta', hasta);
    const qs = params.toString();
    const data = await apiFetch(`/PagoCuentaBancaria/cuenta/${idCuenta}/resumen${qs ? '?' + qs : ''}`);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const obtenerEstadisticasPagosCuenta = async (fechaInicio, fechaFin, tipoDocumentoPago = null) => {
  try {
    const params = new URLSearchParams({
      fechaInicio: fechaInicio.toISOString?.() || fechaInicio,
      fechaFin: fechaFin.toISOString?.() || fechaFin
    });
    if (tipoDocumentoPago) params.append('tipoDocumentoPago', tipoDocumentoPago);
    const data = await apiFetch(`/PagoCuentaBancaria/estadisticas?${params}`);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const crearPagoCuenta = async (payload) => {
  try {
    const data = await apiFetch('/PagoCuentaBancaria', { method: 'POST', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const actualizarPagoCuenta = async (id, payload) => {
  try {
    const data = await apiFetch(`/PagoCuentaBancaria/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e.message, data: null };
  }
};

export const eliminarPagoCuenta = async (id) => {
  try {
    await apiFetch(`/PagoCuentaBancaria/${id}`, { method: 'DELETE' });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

export const buscarPagosCuenta = async (filtros = {}) => {
  try {
    const data = await apiFetch('/PagoCuentaBancaria/search', {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
    return { success: true, data: data || [] };
  } catch (e) {
    return { success: false, error: e.message, data: [] };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  TIPOS_CUENTA, MONEDAS, TIPOS_DOCUMENTO_TITULAR,
  METODOS_PAGO, TIPOS_DOCUMENTO_PAGO,
  formatearMetodoPago, getMetodoPagoIcon, formatearTipoCuenta,
  formatearMoneda, formatearTipoDocumentoPago, formatearDocumentoTitular,
  formatearNumeroCuenta, formatearMonto,
  obtenerBancos, obtenerBancosPagoMovil, obtenerBancoPorId, crearBanco,
  actualizarBanco, eliminarBanco, buscarBancos,
  obtenerCuentasBancarias, obtenerCuentasPorBanco, obtenerCuentasPorDocumento,
  obtenerCuentasPropias, obtenerCuentaPorId, crearCuentaBancaria,
  actualizarCuentaBancaria, eliminarCuentaBancaria,
  obtenerPagosCuenta, obtenerPagosPorDocumento, obtenerPagosPorCuenta,
  obtenerDetallePagoDocumento, obtenerResumenCuenta, obtenerEstadisticasPagosCuenta,
  crearPagoCuenta, actualizarPagoCuenta, eliminarPagoCuenta, buscarPagosCuenta
};